import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, CaretDown, User, WhatsappLogo, MagnifyingGlass } from "@phosphor-icons/react";
import { NAV, SITE, whatsappLink } from "@/data/site";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/LanguageToggle";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18 py-2">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 sm:gap-2.5 group shrink-0 mr-4">
            <img
              src={SITE.logoBox}
              alt="ShipKorea.in logo"
              data-testid="logo-image"
              className="h-9 w-auto sm:h-11 object-contain shrink-0 group-hover:scale-105 transition-transform"
            />
            <span className="font-display font-extrabold text-lg sm:text-xl md:text-2xl tracking-tight leading-none whitespace-nowrap">
              <span className="text-slate-900">Ship</span>
              <span className="text-korea-red">Korea</span>
              <span className="text-amber-500">.in</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV.map((item) =>
              item.children ? (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger
                    data-testid="nav-services-trigger"
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:text-korea-blue rounded-lg hover:bg-slate-50 transition-colors outline-none"
                  >
                    {item.label}
                    <CaretDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-60 rounded-xl">
                    {item.children.map((c) => (
                      <DropdownMenuItem key={c.to} asChild>
                        <Link
                          to={c.to}
                          data-testid={`nav-${c.to.replace(/\//g, "")}`}
                          className="cursor-pointer text-sm"
                        >
                          {c.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.replace(/\//g, "")}`}
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-korea-blue rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            <div className="hidden md:block">
              <LanguageToggle />
            </div>
            <Link to="/track" className="hidden md:block">
              <Button
                data-testid="header-track-btn"
                className="rounded-full bg-korea-blue hover:bg-korea-bluehover px-4 h-10 font-semibold text-white"
              >
                <MagnifyingGlass className="h-4 w-4 mr-1.5" weight="bold" /> Track
              </Button>
            </Link>
            <a href="https://www.shipoms.in/login" className="hidden lg:block">
              <Button
                data-testid="header-signin-btn"
                variant="outline"
                className="rounded-full border-slate-300 px-4 h-10 font-semibold"
              >
                <User className="h-4 w-4 mr-1.5" /> Sign In
              </Button>
            </a>

            <a href={whatsappLink()} target="_blank" rel="noreferrer nofollow" className="hidden sm:block">
              <Button
                data-testid="header-whatsapp-btn"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-5 h-10 font-semibold"
              >
                <WhatsappLogo weight="fill" className="mr-1.5 h-4 w-4" /> WhatsApp Us
              </Button>
            </a>

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="xl:hidden">
                <button
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                  className="grid place-items-center h-10 w-10 rounded-lg border border-slate-200 bg-white"
                >
                  <List className="h-5 w-5 text-slate-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-96 overflow-y-auto">
                <SheetTitle className="sr-only">ShipKorea.in Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Site navigation links for ShipKorea.in courier services</SheetDescription>
                <div className="flex items-center gap-2 mb-6">
                  <img src={SITE.logoBox} alt="ShipKorea.in logo" fetchPriority="high" decoding="async" className="h-10 w-auto object-contain" />
                  <span className="font-display font-extrabold text-xl tracking-tight leading-none">
                    <span className="text-slate-900">Ship</span>
                    <span className="text-korea-red">Korea</span>
                    <span className="text-amber-500">.in</span>
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {NAV.map((item) =>
                    item.children ? (
                      <div key={item.label} className="py-1">
                        <p className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                          {item.label}
                        </p>
                        {item.children.map((c) => (
                          <SheetClose asChild key={c.to}>
                            <Link
                              to={c.to}
                              data-testid={`mnav-${c.to.replace(/\//g, "")}`}
                              className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
                            >
                              {c.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    ) : (
                      <SheetClose asChild key={item.to}>
                        <Link
                          to={item.to}
                          data-testid={`mnav-${item.to.replace(/\//g, "")}`}
                          className="px-3 py-2.5 rounded-lg text-sm font-medium text-slate-800 hover:bg-slate-50"
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    )
                  )}
                  <SheetClose asChild>
                    <Link to="/track" className="mt-4 block">
                      <Button data-testid="mnav-track-btn" className="w-full rounded-full bg-korea-blue hover:bg-korea-bluehover h-11 font-semibold text-white">
                        <MagnifyingGlass className="mr-1.5 h-4 w-4" weight="bold" /> Track Shipment
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <a href={whatsappLink()} target="_blank" rel="noreferrer nofollow" className="mt-2 block">
                      <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-11 font-semibold">
                        <WhatsappLogo weight="fill" className="mr-1.5 h-4 w-4" /> WhatsApp Us
                      </Button>
                    </a>
                  </SheetClose>
                  <SheetClose asChild>
                    <a href="https://www.shipoms.in/login" className="mt-2 block">
                      <Button
                        data-testid="mnav-signin-btn"
                        variant="outline"
                        className="w-full rounded-full border-slate-300 h-11 font-semibold"
                      >
                        <User className="h-4 w-4 mr-1.5" /> Sign In
                      </Button>
                    </a>
                  </SheetClose>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Language / 언어</p>
                    <LanguageToggle />
                  </div>
                  <a href={`tel:${SITE.phone}`} className="mt-2 text-center text-sm text-slate-500">
                    Call {SITE.phoneDisplay}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
